import { useEffect, useState } from "react";
import AdminLayout, { PageHeader } from "../../components/admin/AdminLayout";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { SortableProvider, SortableItems, SortableItem, DragHandle } from "../../components/admin/SortableList";
import SeoFields from "../../components/admin/SeoFields";

export default function Categories() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", kind: "product", description: "" });

  const load = async () => {
    const { data } = await api.get("/admin/categories");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", kind: "product", description: "", slug: "", meta_title: "", meta_description: "", meta_keywords: "", og_image: "", canonical_url: "", robots: "", locale: "en-IN" });
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ name: it.name, kind: it.kind, description: it.description || "", slug: it.slug || "", meta_title: it.meta_title || "", meta_description: it.meta_description || "", meta_keywords: it.meta_keywords || "", og_image: it.og_image || "", canonical_url: it.canonical_url || "", robots: it.robots || "", locale: it.locale || "en-IN" });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/admin/categories/${editing.id}`, form);
      } else {
        await api.post("/admin/categories", form);
      }
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this category?")) return;
    try { await api.delete(`/admin/categories/${id}`); load(); toast.success("Deleted"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <AdminLayout>
      <PageHeader
        title="Categories"
        subtitle="Organize products and blog posts."
        actions={
          <Button onClick={openNew} data-testid="add-category-btn" className="bg-[#C2410C] hover:bg-[#9A3412] text-white">
            <Plus size={16} className="mr-2" />New category
          </Button>
        }
      />
      <div className="p-8">
        <SortableProvider items={items} setItems={setItems} reorderEndpoint="/admin/reorder/categories">
          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="categories-table">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F8F6] text-left text-[10px] tracking-[0.2em] uppercase text-[#4B5563]">
                <tr>
                  <th className="px-3 py-3 w-10"></th>
                  <th className="px-6 py-3">Name</th>
                  <th className="px-6 py-3">Kind</th>
                  <th className="px-6 py-3">Slug</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]">
                {items.length === 0 && (
                  <tr><td colSpan={5} className="px-6 py-10 text-center text-[#4B5563]">No categories yet.</td></tr>
                )}
                <SortableItems items={items}>
                  {items.map((it) => (
                    <SortableItem key={it.id} id={it.id} as="tr" data-testid={`category-row-${it.id}`}>
                      {(handleProps) => (
                        <>
                          <td className="px-3 py-3"><DragHandle handleProps={handleProps} /></td>
                          <td className="px-6 py-3 font-medium text-[#1F2937]">{it.name}</td>
                          <td className="px-6 py-3">
                            <span className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded bg-[#C2410C]/10 text-[#C2410C]">
                              {it.kind}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-[#4B5563] font-mono text-xs">{it.slug}</td>
                          <td className="px-6 py-3 text-right">
                            <button onClick={() => openEdit(it)} data-testid={`edit-${it.id}`} className="text-[#4B5563] hover:text-[#C2410C] mr-3"><PencilSimple size={16} /></button>
                            <button onClick={() => remove(it.id)} data-testid={`delete-${it.id}`} className="text-[#4B5563] hover:text-[#EF4444]"><Trash size={16} /></button>
                          </td>
                        </>
                      )}
                    </SortableItem>
                  ))}
                </SortableItems>
              </tbody>
            </table>
          </div>
        </SortableProvider>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit category" : "New category"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cat-name" />
            </div>
            {!editing && (
              <div className="space-y-2">
                <Label>Kind</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="cat-kind"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="product">Product</SelectItem>
                    <SelectItem value="blog">Blog</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="cat-desc" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-category" className="bg-[#C2410C] hover:bg-[#9A3412]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
